import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useRequirementPositionsQuery } from 'queries/requirements'
import { useRequirementDetailContextValue } from './RequirementDetailProvider'

type AssociatedPositionsContextState = {
  state: {
    items: NonNullable<
      ReturnType<typeof useRequirementPositionsQuery>['data']
    >['pages'][number]['items']
    dataUpdatedAt: number
    isFetching: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    isError: boolean
    error: string
  }
  actions: {
    refetch: () => void
    loadMore: () => void
  }
}

const [AssociatedPositionsContext, useAssociatedPositionsContextValue] =
  createContext<AssociatedPositionsContextState>({
    name: 'AssociatedPositionsContext',
  })

export const AssociatedPositionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    state: { id: requirementId },
  } = useRequirementDetailContextValue()

  const {
    data,
    isPending: isPositionsLoading,
    isError: isPositionsError,
    error: positionsError,
    dataUpdatedAt,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useRequirementPositionsQuery({
    requirementId,
    pageSize: 10,
  })

  if (isPositionsLoading) {
    return <Loading />
  }

  if (isPositionsError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const items = data.pages.flatMap((page) => page.items)

  const value: AssociatedPositionsContextState = {
    state: {
      items,
      dataUpdatedAt,
      isFetching,
      isFetchingNextPage,
      hasNextPage: Boolean(hasNextPage),
      isError: isPositionsError,
      error: parseErrorMessage(positionsError),
    },
    actions: {
      refetch,
      loadMore: () => {
        void fetchNextPage()
      },
    },
  }

  return (
    <AssociatedPositionsContext.Provider value={value}>
      {children}
    </AssociatedPositionsContext.Provider>
  )
}

export { useAssociatedPositionsContextValue }
